"use client";
import { use } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { redirect } from "next/navigation";
import CareerDetail from "@/components/CareerDetails/CareerDetails";
import SkeletonCareerDetails from "@/components/CareerDetails/SkeletonCareerDetails";
import { useCareerOrCourseDetail } from "@/hooks/useCareerOrCourseDetail";
import { useClassroomLookup } from "@/hooks/useClassroomLookup";
import { findCourseClassroom } from "@/services/courseService";
import { COURSE_NOT_FOUND_MESSAGE } from "@/constants/messages";
import { normalizeClassroom } from "@/utils/normalizeClassroom";
import { generateLinkWithParams } from "@/utils/generateLinkWithParams";

const CoursePageClient = ({ params }: { params: Promise<{ name: string }> }) => {
  const { name } = use(params);
  const searchParams = useSearchParams();
  const rawType = searchParams.get("type");
  const rawClassroom = searchParams.get("classroom");
  const rawMode = searchParams.get("mode");
  const rawInitialDate = searchParams.get("initialDate");
  const rawOfficeLocation = searchParams.get("officeLocation");

  const utmParams = {
    utm_source: searchParams.get('utm_source') || undefined,
    utm_medium: searchParams.get('utm_medium') || undefined,
    utm_campaign: searchParams.get('utm_campaign') || undefined,
    utm_id: searchParams.get('utm_id') || undefined,
  };

  const { resolvedType, resolvedMode, resolvedInitialDate, resolvedOfficeLocation, resolvedSchedule, lookupError, isLoading } = useClassroomLookup({
    rawType,
    rawClassroom,
    rawMode,
    rawInitialDate,
    rawOfficeLocation,
    lookupFn: async () => {
      const matched = await findCourseClassroom(name, rawClassroom ?? '');
      if (!matched?.course) return null;
      return {
        type: matched.course,
        initialDate: matched.initialDate,
        mode: matched.mode,
        officeLocation: matched.officeLocation,
        schedule: matched.schedule,
      };
    },
    extraDeps: [name],
  });

  const slugCourseName = resolvedType ? null : name;
  const { career, error } = useCareerOrCourseDetail(resolvedType, slugCourseName, "course", !!isLoading);
  if (error || lookupError) {
    return (
      <div className="px-4 py-8 text-center text-gray-800 bg-white lg:py-12 md:px-8 lg:px-32">
        <h1 className="mb-4 text-2xl font-semibold">
          {COURSE_NOT_FOUND_MESSAGE.title}
        </h1>
        <p className="mb-4">{COURSE_NOT_FOUND_MESSAGE.description}</p>
        <Link href="/">
          <button className="px-6 py-2 text-white bg-red-600 hover:bg-red-800">
            {COURSE_NOT_FOUND_MESSAGE.buttonLabel}{lookupError}
          </button>
        </Link>
      </div>
    );
  }
  if (!career) {
    return (
      <div className="px-4 py-8 bg-white md:px-8 lg:px-32">
        <SkeletonCareerDetails />
      </div>
    );
  }
  const normalizedClass = normalizeClassroom(rawClassroom);
  const checkoutUrl = generateLinkWithParams(
    `https://inscripciones.rollingcodeschool.com/live-courses/checkout/new/${normalizedClass}`,
    null,
    null,
    utmParams
  );
  if (!career.fullDescription) {
    redirect(checkoutUrl);
  }
  return (
    <div className="px-4 py-8 bg-white md:px-8 lg:px-16">
      <CareerDetail
        career={career}
        classroom={rawClassroom}
        mode={resolvedMode}
        initialDate={resolvedInitialDate}
        officeLocation={resolvedOfficeLocation}
        schedule={resolvedSchedule}
      />
    </div>
  );
};

export default CoursePageClient;
